import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { geohashQueryBounds, distanceBetween } from "geofire-common";
import { firebaseDatabase } from "../firebase";
import { ref, query, orderByChild, startAt, endAt, get } from "firebase/database";

/**
 * Calcola il raggio (in km) dell'area visibile sulla mappa.
 */
export function getRadius(center, zoom, mapWidth, mapHeight) {
    return (metersPerPixels(center[0], zoom) * (getHypotenuse(mapWidth, mapHeight) / 2)) / 1000;
}

function getHypotenuse(w, h) {
    return Math.sqrt(Math.pow(w, 2) + Math.pow(h, 2));
}

function metersPerPixels(lat, zoom) {
    return 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
}

/**
 * Esegue una query geospaziale sul Realtime Database usando geofire-common.
 *
 * @param {string} basePath         - path nel database es. '/drinkingFountainsGeoHashed'
 * @param {[number,number]} center  - [lat, lng]
 * @param {number} radiusKm         - raggio in km
 * @returns {Promise<Map<string, object>>} - mappa key → { key, l, g, ...altri campi }
 */
export async function runGeoQuery(basePath, center, radiusKm) {
    const bounds = geohashQueryBounds(center, radiusKm * 1000);
    const baseRef = ref(firebaseDatabase, basePath);
    const results = new Map();

    const snapshots = await Promise.all(
        bounds.map(([start, end]) =>
            get(query(baseRef, orderByChild('g'), startAt(start), endAt(end)))
        )
    );

    snapshots.forEach(snap => {
        snap.forEach(child => {
            const data = child.val();
            if (!data || !data.l) return;
            const [lat, lng] = data.l;
            const dist = distanceBetween([lat, lng], center);
            if (dist <= radiusKm) {
                results.set(child.key, { ...data, key: child.key });
            }
        });
    });

    return results;
}

/**
 * Legge un singolo record dal database dato il basePath e la key.
 * Equivalente a geoFire.ref().child(key).get()
 */
export async function getGeoRecord(basePath, key) {
    const nodeRef = ref(firebaseDatabase, `${basePath}/${key}`);
    const snap = await get(nodeRef);
    if (!snap.exists()) return null;
    return { ...snap.val(), key: snap.key };
}

/**
 * Aggiorna campi di un record esistente nel database.
 * Equivalente a geoFire.ref().child(key).update(data)
 */
export async function updateGeoRecord(basePath, key, data) {
    const { update } = await import("firebase/database");
    const nodeRef = ref(firebaseDatabase, `${basePath}/${key}`);
    await update(nodeRef, data);
}

/**
 * Elimina un record dal database.
 * Equivalente a geoFire.ref().update({ [key]: null })
 */
export async function deleteGeoRecord(basePath, key) {
    const { remove } = await import("firebase/database");
    const nodeRef = ref(firebaseDatabase, `${basePath}/${key}`);
    await remove(nodeRef);
}

/**
 * Hook che sostituisce useGeoQuery + geoQuery.on("key_entered/exited").
 *
 * Invece dell'approccio event-based di GeoFire v5, fa una query completa
 * ogni volta che cambiano center/radius/geoPath, poi chiama i callback
 * onKeyEntered e onKeyExited per aggiornare lo stato del chiamante.
 *
 * @param {{ center: [number,number], radius: number }} queryCriteria
 * @param {any[]} deps           - dipendenze extra (es. [dim])
 * @param {string} geoPath       - path nel database (da GEO_PATHS)
 * @param {function} onKeyEntered - (key, data) => void
 * @param {function} onKeyExited  - (key) => void
 */
export function useGeoQuery({ center, radius }, deps, geoPath, onKeyEntered, onKeyExited) {
    const prevKeysRef = useRef(new Set());
    const abortRef = useRef(false);

    const doQuery = useCallback(
        debounce(async (center, radius, geoPath) => {
            if (!geoPath || !center || !radius) return;
            abortRef.current = false;

            try {
                const results = await runGeoQuery(geoPath, center, radius);
                if (abortRef.current) return;

                const newKeys = new Set(results.keys());
                const prevKeys = prevKeysRef.current;

                // key_entered: chiavi nuove che prima non c'erano
                newKeys.forEach(key => {
                    if (!prevKeys.has(key)) {
                        onKeyEntered(key, results.get(key));
                    }
                });

                // key_exited: chiavi che erano presenti ma ora fuori dal raggio
                prevKeys.forEach(key => {
                    if (!newKeys.has(key)) {
                        onKeyExited(key);
                    }
                });

                prevKeysRef.current = newKeys;
            } catch (e) {
                console.error("useGeoQuery error:", e);
            }
        }, 500),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [geoPath, onKeyEntered, onKeyExited]
    );

    useEffect(() => {
        doQuery(center, radius, geoPath);
        return () => {
            abortRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center?.[0], center?.[1], radius, geoPath, ...deps]);
}